import React, { useState } from 'react';
import { FiAlertCircle, FiLoader, FiCheck, FiClock, FiX, FiXCircle } from 'react-icons/fi';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  useGetPushDestinations,
  useSendStudyToDestination,
  useCancelPushBatch,
  useEchoDestination,
} from '../../../helpers/api/dicom-send/dicom-send';
import { messageI18n } from '../../../helpers/i18n/toastI18n';

interface ServerSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectServer?: (server: Record<string, unknown>) => void;
  studyInstanceUid?: string;
}

interface ServerWithStatus {
  id: number;
  server_ae_title: string;
  server_host?: string;
  server_port?: number;
  has_pending_jobs?: boolean;
  last_batch_id?: string | null;
  last_batch_status?: 'PENDING' | 'COMPLETED' | 'FAILED' | 'PARTIAL' | 'CANCELLED' | null;
  last_batch_total?: number;
  last_batch_completed?: number;
  last_batch_pending?: number;
  last_batch_failed?: number;
  last_activity_at?: string | null;
}

export const ServerSelectionModal: React.FC<ServerSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectServer,
  studyInstanceUid,
}) => {
  const [sendingToServerId, setSendingToServerId] = useState<number | null>(
    null
  );
  const [cancellingServerId, setCancellingServerId] = useState<number | null>(
    null
  );
  const [sentServerIds, setSentServerIds] = useState<Set<number>>(new Set());
  const [echoingServerId, setEchoingServerId] = useState<number | null>(null);
  const [echoResults, setEchoResults] = useState<
    Record<number, { reachable: boolean; message: string }>
  >({});

  const {
    data: serversData,
    isLoading,
    error,
    refetch,
  } = useGetPushDestinations(studyInstanceUid, {
    enabled: isOpen,
    refetchInterval: (query: any) => {
      const rows = query?.state?.data?.data ?? [];
      const busy = rows.some((r: ServerWithStatus) => r.has_pending_jobs);
      return busy ? 3000 : false;
    },
  });

  const sendStudyMutation = useSendStudyToDestination(
    response => {
      const data = response.data?.data;
      if (data) {
        messageI18n.success('server.studyQueued', {
          queuedJobs: data.queued_jobs,
          destinationAeTitle: data.destination ?? data.destination_ae_id,
        });
        const justSent = sendingToServerId!;
        setSentServerIds(prev => new Set(prev).add(justSent));
        setTimeout(() => {
          setSentServerIds(prev => {
            const next = new Set(prev);
            next.delete(justSent);
            return next;
          });
        }, 4000);
        onSelectServer?.({ ...data, batch_id: data.batch_id });
      }
      setSendingToServerId(null);
      refetch();
    },
    () => {
      messageI18n.error('server.sendStudyFailed');
      setSendingToServerId(null);
    },
    false
  );

  const cancelBatchMutation = useCancelPushBatch(
    () => {
      messageI18n.success('server.syncCancelled');
      setCancellingServerId(null);
      refetch();
    },
    () => {
      messageI18n.error('server.cancelSyncFailed');
      setCancellingServerId(null);
    },
    false
  );

  const echoMutation = useEchoDestination(
    response => {
      const data = response.data?.data;
      const id = data?.destination_ae_id;
      if (id != null) {
        setEchoResults(prev => ({
          ...prev,
          [id]: {
            reachable: !!data.reachable,
            message: response.data?.message ?? '',
          },
        }));
      }
      setEchoingServerId(null);
    },
    () => {
      setEchoingServerId(null);
      messageI18n.error('server.echoFailed');
    }
  );

  const handleEcho = async (server: ServerWithStatus) => {
    setEchoingServerId(server.id);
    try {
      await echoMutation.mutateAsync({ destinationAeId: server.id });
    } catch {
      // Handled in onError
    }
  };

  const handleSendToServer = async (server: ServerWithStatus) => {
    if (!studyInstanceUid) {
      messageI18n.error('server.noStudySelected');
      return;
    }

    setSendingToServerId(server.id);

    try {
      await sendStudyMutation.mutateAsync({
        study_instance_uid: studyInstanceUid,
        destinationAeId: server.id,
      });
    } catch {
      // Error is handled in onError callback
    }
  };

  const handleCancelSync = async (server: ServerWithStatus) => {
    if (!server.last_batch_id) {
      messageI18n.error('server.noBatchToCancel');
      return;
    }

    setCancellingServerId(server.id);

    try {
      await cancelBatchMutation.mutateAsync({
        batchId: server.last_batch_id,
      });
    } catch {
      // Error is handled in onError callback
    }
  };

  const getStatusDisplay = (
    server: ServerWithStatus
  ): { text: string; className: string; icon?: React.ReactNode } => {
    if (sentServerIds.has(server.id)) {
      return {
        text: 'Queued for sending',
        className: 'text-green-600 dark:text-green-400',
        icon: <FiCheck className="h-3 w-3" />,
      };
    }

    if (sendingToServerId === server.id) {
      return {
        text: 'Sending...',
        className: 'text-blue-600 dark:text-blue-400',
      };
    }

    if (server.last_batch_status === 'PENDING') {
      const done = server.last_batch_completed || 0;
      const total = server.last_batch_total || 0;
      return {
        text: total ? `Syncing (${done}/${total} instances)` : 'Syncing',
        className: 'text-amber-600 dark:text-amber-400',
        icon: <FiClock className="h-3 w-3" />,
      };
    }

    if (server.last_batch_status === 'COMPLETED') {
      const total = server.last_batch_total || server.last_batch_completed || 0;
      return {
        text: total ? `Synced (${total}/${total} instances)` : 'Synced',
        className: 'text-green-600 dark:text-green-400',
        icon: <FiCheck className="h-3 w-3" />,
      };
    }

    if (server.last_batch_status === 'FAILED') {
      const failed = server.last_batch_failed || 0;
      return {
        text: failed ? `Sync failed (${failed} instance(s))` : 'Sync failed',
        className: 'text-red-600 dark:text-red-400',
        icon: <FiX className="h-3 w-3" />,
      };
    }

    if (server.last_batch_status === 'CANCELLED') {
      return {
        text: 'Sync cancelled',
        className: 'text-slate-500 dark:text-slate-400',
        icon: <FiXCircle className="h-3 w-3" />,
      };
    }

    if (server.last_batch_status === 'PARTIAL') {
      const completed = server.last_batch_completed || 0;
      const total = server.last_batch_total || 0;
      return {
        text: `Partially synced (${completed}/${total} instances)`,
        className: 'text-amber-600 dark:text-amber-400',
        icon: <FiAlertCircle className="h-3 w-3" />,
      };
    }

    return {
      text: 'Not synced',
      className: 'text-slate-500 dark:text-slate-400',
    };
  };

  const getButtonState = (server: ServerWithStatus) => {
    const isSending = sendingToServerId === server.id;
    const isCancelling = cancellingServerId === server.id;
    const isSent = sentServerIds.has(server.id);
    const hasPendingJobs = server.has_pending_jobs || false;
    const canCancel = hasPendingJobs && !!server.last_batch_id;
    const isDisabled = isSending || isCancelling || sendingToServerId !== null || cancellingServerId !== null;

    return { isSending, isCancelling, isSent, hasPendingJobs, canCancel, isDisabled };
  };

  if (!isOpen) {
    return null;
  }

  const servers: ServerWithStatus[] = serversData?.data || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-gray-700 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Endpoints
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Send or re-send this study to one or more of your endpoints below
          </p>
        </div>

        {/* Content */}
        <Modal.Body>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-slate-600 dark:text-slate-400">
                Loading servers...
              </span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FiAlertCircle className="h-12 w-12 text-red-500 mb-3" />
              <p className="text-red-600 dark:text-red-400 font-medium">
                Failed to load servers
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Please try again later
              </p>
            </div>
          ) : servers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-slate-600 dark:text-slate-400 font-medium">
                No endpoints available
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Configure a server that supports C-STORE to send exams
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {servers.map(server => {
                const { isSending, isCancelling, isSent, hasPendingJobs, canCancel, isDisabled } =
                  getButtonState(server);
                const statusInfo = getStatusDisplay(server);

                return (
                  <div
                    key={server.id}
                    className="flex items-center justify-between py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 px-2 rounded transition-colors"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-title-page-color-100 dark:text-white text-base">
                        {server.server_ae_title}
                      </h4>
                      {(server.server_host || server.server_port) && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {server.server_host}:{server.server_port}
                          {server.tls_enabled ? ' · TLS' : ''}
                        </p>
                      )}
                      <p
                        className={`text-sm mt-0.5 flex items-center gap-1 ${statusInfo.className}`}
                      >
                        {statusInfo.icon}
                        {statusInfo.text}
                      </p>
                      {echoResults[server.id] && (
                        <p
                          className={`text-xs mt-0.5 flex items-center gap-1 ${
                            echoResults[server.id].reachable
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {echoResults[server.id].reachable ? (
                            <FiCheck className="h-3 w-3" />
                          ) : (
                            <FiAlertCircle className="h-3 w-3" />
                          )}
                          {echoResults[server.id].message}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleEcho(server)}
                        disabled={echoingServerId !== null}
                        title="Verify the destination is reachable (C-ECHO)"
                      >
                        {echoingServerId === server.id ? (
                          <>
                            <FiLoader className="animate-spin mr-1.5 h-3 w-3" />
                            Testing
                          </>
                        ) : (
                          'Test'
                        )}
                      </Button>
                      {canCancel && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleCancelSync(server)}
                          disabled={isCancelling || cancellingServerId !== null}
                          title="Cancel pending sync"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                        >
                          {isCancelling ? (
                            <>
                              <FiLoader className="animate-spin mr-1.5 h-3 w-3" />
                              Cancelling
                            </>
                          ) : (
                            <>
                              <FiXCircle className="mr-1.5 h-3 w-3" />
                              Cancel
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleSendToServer(server)}
                        disabled={isDisabled || hasPendingJobs}
                        title={
                          hasPendingJobs
                            ? 'A sync is already in progress'
                            : undefined
                        }
                      >
                        {isSending ? (
                          <>
                            <FiLoader className="animate-spin mr-1.5 h-3 w-3" />
                            Sending
                          </>
                        ) : isSent ? (
                          <>
                            <FiCheck className="mr-1.5 h-3 w-3" />
                            Sent
                          </>
                        ) : hasPendingJobs ? (
                          <>
                            <FiClock className="mr-1.5 h-3 w-3" />
                            Syncing
                          </>
                        ) : (
                          'Send'
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};
